import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Table,
  Select,
  Button,
  Space,
  Typography,
  Tag,
  message,
  Popconfirm,
  List,
  Avatar,
} from "antd";
import { ThunderboltOutlined, DeleteOutlined } from "@ant-design/icons";

const { Text } = Typography;

const getRowKey = (r) => r.loanNewId ?? `${r.DATA_TYPE}_${r.CONTNO}`;

const fullName = (r) =>
  `${r?.SNAM || ""}${r?.NAME1 || ""} ${r?.NAME2 || ""}`.trim() || "-";

const AVATAR_COLORS = [
  "#1677ff",
  "#52c41a",
  "#fa8c16",
  "#eb2f96",
  "#722ed1",
  "#13c2c2",
  "#faad14",
  "#f5222d",
];

// ไล่สี Avatar ตามชื่อ ให้แต่ละคนได้สีเดิมเสมอ
const colorForName = (name) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

// สลับลำดับ array แบบสุ่ม (Fisher-Yates)
const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const AssignLawyerModal = ({
  open,
  onClose,
  contracts,
  lawyerOptions,
  onSuccess,
}) => {
  const [rows, setRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setRows(
        (contracts || []).map((r) => ({
          key: getRowKey(r),
          record: r,
          lawyerName: undefined,
        })),
      );
    }
  }, [open, contracts]);

  // สุ่มผู้รับผิดชอบจาก lawyerOptions ให้ทุกสัญญาที่เหลืออยู่ในรายการ
  const handleRandomAssign = () => {
    if (!rows.length) {
      message.warning("ไม่มีสัญญาที่จะกำหนดผู้รับผิดชอบ");
      return;
    }
    if (!lawyerOptions?.length) {
      message.warning("ไม่พบรายชื่อผู้รับผิดชอบ");
      return;
    }
    const shuffledLawyers = shuffle(lawyerOptions);
    setRows((prev) =>
      prev.map((row, i) => ({
        ...row,
        lawyerName: shuffledLawyers[i % shuffledLawyers.length].value,
      })),
    );
  };

  const handleChangeLawyer = (key, value) => {
    setRows((prev) =>
      prev.map((row) =>
        row.key === key ? { ...row, lawyerName: value } : row,
      ),
    );
  };

  const handleRemoveRow = (key) => {
    setRows((prev) => prev.filter((row) => row.key !== key));
  };

  // สรุปจำนวนสัญญาที่แต่ละคนได้รับมอบหมาย
  const lawyerCounts = useMemo(() => {
    const counts = {};
    rows.forEach((row) => {
      if (row.lawyerName) {
        counts[row.lawyerName] = (counts[row.lawyerName] || 0) + 1;
      }
    });
    return Object.entries(counts)
      .map(([value, count]) => ({
        value,
        label: lawyerOptions?.find((o) => o.value === value)?.label || value,
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }, [rows, lawyerOptions]);

  const handleConfirm = async () => {
    if (!rows.length) {
      message.warning("ไม่มีสัญญาที่จะกำหนดผู้รับผิดชอบ");
      return;
    }
    if (rows.some((row) => !row.lawyerName)) {
      message.warning("กรุณากำหนดผู้รับผิดชอบให้ครบทุกสัญญา");
      return;
    }

    try {
      setSubmitting(true);
      // TODO: เรียก API บันทึกผู้รับผิดชอบจริงตรงนี้
      const payload = rows.map((row) => ({
        contno: row.record.CONTNO,
        dataType: row.record.DATA_TYPE,
        lawyerName: row.lawyerName,
      }));
      console.log("📤 assign lawyer payload:", payload);
      onSuccess?.(rows);
      message.success("กำหนดผู้รับผิดชอบสำเร็จ");
      onClose(false);
    } catch (error) {
      console.error("❌ Error:", error);
      message.error("กำหนดผู้รับผิดชอบไม่สำเร็จ");
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 60,
      render: (_, __, i) => i + 1,
    },
    {
      title: "เลขสัญญา / ชื่อ-นามสกุล",
      key: "detail",
      align: "left",
      render: (_, row) => (
        <div style={{ fontSize: 13, lineHeight: 1.6 }}>
          <div>
            <Text strong>{row.record.CONTNO || "-"}</Text>
          </div>
          <div style={{ color: "#888" }}>{fullName(row.record)}</div>
        </div>
      ),
    },
    {
      title: "ผู้รับผิดชอบ",
      key: "lawyerName",
      align: "left",
      width: 220,
      render: (_, row) => (
        <Select
          style={{ width: "100%" }}
          options={lawyerOptions}
          value={row.lawyerName}
          onChange={(value) => handleChangeLawyer(row.key, value)}
          placeholder="เลือกผู้รับผิดชอบ"
          showSearch
          allowClear
        />
      ),
    },
    {
      title: "",
      key: "action",
      align: "center",
      width: 60,
      render: (_, row) => (
        <Popconfirm
          title="ลบสัญญานี้ออกจากรายการ?"
          onConfirm={() => handleRemoveRow(row.key)}
          okText="ลบ"
          cancelText="ยกเลิก"
        >
          <Button danger type="text" icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Modal
      title="กำหนดผู้รับผิดชอบ"
      open={open}
      onCancel={() => onClose(false)}
      footer={null}
      width={720}
      destroyOnHidden
    >
      <Space
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <Tag color="blue">{rows.length} สัญญา</Tag>
        <Button icon={<ThunderboltOutlined />} onClick={handleRandomAssign}>
          สุ่มผู้รับผิดชอบ
        </Button>
      </Space>

      <Table
        size="small"
        columns={columns}
        dataSource={rows}
        rowKey="key"
        pagination={false}
        scroll={{ y: 400 }}
        locale={{ emptyText: "ไม่มีสัญญาที่เลือก" }}
      />

      {lawyerCounts.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 10,
            }}
          >
            <Text strong style={{ fontSize: 14 }}>
              สรุปจำนวนเคสต่อผู้รับผิดชอบ
            </Text>
            <Tag color="processing">{lawyerCounts.length} คน</Tag>
          </div>
          <List
            size="small"
            bordered
            style={{ borderRadius: 8, background: "#fff" }}
            dataSource={lawyerCounts}
            renderItem={(item) => (
              <List.Item
                style={{ padding: "10px 16px" }}
                extra={
                  <Tag
                    color="blue"
                    style={{ fontSize: 13, fontWeight: 600, margin: 0 }}
                  >
                    {item.count} เคส
                  </Tag>
                }
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      size={32}
                      style={{
                        backgroundColor: colorForName(item.label),
                        fontWeight: 600,
                      }}
                    >
                      {item.label.charAt(0).toUpperCase()}
                    </Avatar>
                  }
                  title={
                    <span style={{ fontSize: 13, fontWeight: 500 }}>
                      {item.label}
                    </span>
                  }
                />
              </List.Item>
            )}
          />
        </div>
      )}

      <div style={{ textAlign: "center", marginTop: 16 }}>
        <Button
          onClick={() => onClose(false)}
          style={{ marginRight: 16 }}
          size="large"
        >
          ปิด
        </Button>
        <Popconfirm
          title="ยืนยันกำหนดผู้รับผิดชอบ?"
          description="คุณต้องการบันทึกผู้รับผิดชอบตามรายการนี้ใช่หรือไม่"
          onConfirm={handleConfirm}
          okText="ยืนยัน"
          cancelText="ยกเลิก"
        >
          <Button type="primary" size="large" loading={submitting}>
            ยืนยัน
          </Button>
        </Popconfirm>
      </div>
    </Modal>
  );
};

export default AssignLawyerModal;
